import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  notification,
  Row,
  Col,
  Alert,
  Spin,
  Tag,
} from "antd";
import { IoArrowBack } from "react-icons/io5";
import { useSelector } from "react-redux";
import mainContext from "../context/MainContext/mainContext";
import BillingForm from "./BillingForm";
import InventoryList from "./InventoryList";
import GrandTotal from "./GrandTotal";
import TotalProductionCost from "./TotalProductionCost";
import { buildBillingInventoryRows } from "../../../../../utils/billingInvMatch";
import { isGujaratBilling } from "../../../../../utils/billingGstUtils";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const DEFAULT_INVOICE_REMARK =
  "Being the charges for Advertisement through display your ad on Outdoor Hoarding sites at";

const ConsolidatedBillingDetails = () => {
  const {
    consolidatedBillingIds,
    setConsolidatedMode,
    setConsolidatedBillingIds,
    setFetchToggle,
    financialYear,
  } = useContext(mainContext);

  const userId = useSelector((state) => state.user._id);
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [billingSources, setBillingSources] = useState([]);
  const [billingData, setBillingData] = useState({
    gstNumber: null,
    state: null,
    parentCompanyName: null,
    brandName: null,
    campaignName: null,
    inventories: [],
    suffixInventories: [],
    HSN: "",
    IGST: 0,
    SGST: 0,
    CGST: 0,
    invoiceRemark: DEFAULT_INVOICE_REMARK,
    invoiceSubRemark: "",
    billingDate: new Date().toISOString(),
    duePrintingAmount: 0,
    dueMountingAmount: 0,
    isConsolidated: true,
  });
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [manualPrintingCharge, setManualPrintingCharge] = useState(undefined);
  const [manualMountingCharge, setManualMountingCharge] = useState(undefined);
  const [isPrintingChargeSelected, setIsPrintingChargeSelected] = useState(true);
  const [isMountingChargeSelected, setIsMountingChargeSelected] = useState(true);

  const loadBillings = async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        consolidatedBillingIds.map(async (billingId) => {
          const res = await fetch(
            `${apiBaseUrl}/inventoryManagement/getBillingInfoById?id=${billingId}`,
            { method: "GET", credentials: "include" }
          );
          const json = await res.json();
          if (!res.ok) throw new Error(json?.message || "Failed to load billing");
          return json;
        })
      );

      const sources = responses.map((responseData) => {
        const info = responseData.billingInfo;
        const so = info.salesOrderId;
        const bdf = so?.bdfId || {};
        const brandId = bdf?.brandId || {};
        const billedInvs = info.inventories || [];
        const rows = buildBillingInventoryRows(bdf?.inventories, billedInvs).map(
          (row) => ({
            ...row,
            sourceBillingId: String(info._id),
            sourceSalesOrderId: String(so?._id),
            soStamp: so?.stamp,
            brandName: brandId?.brandName,
            campaignName: bdf?.campaignName,
          })
        );

        return {
          billingId: String(info._id),
          salesOrderId: String(so?._id),
          stamp: so?.stamp,
          brandName: brandId?.brandName,
          campaignName: bdf?.campaignName,
          duePrintingAmount: Number(info.duePrintingAmount) || 0,
          dueMountingAmount: Number(info.dueMountingAmount) || 0,
          inventories: rows,
          billedInvs,
          gstNumber:
            info?.partyCompany?.gstNumber ||
            bdf?.parentCompanyId?.gstNumber ||
            bdf?.brandId?.partyCompany?.gstNumber ||
            null,
          state:
            info?.partyCompany?.state ||
            bdf?.parentCompanyId?.state ||
            bdf?.brandId?.partyCompany?.state ||
            null,
          parentCompanyName:
            bdf?.parentCompanyId?.name ||
            bdf?.parentCompanyName ||
            bdf?.brandId?.partyCompany?.name ||
            null,
        };
      });

      const first = sources[0];
      const allInventories = sources.flatMap((s) => s.inventories);
      const totalDuePrint = sources.reduce(
        (sum, s) => sum + s.duePrintingAmount,
        0
      );
      const totalDueMount = sources.reduce(
        (sum, s) => sum + s.dueMountingAmount,
        0
      );

      setBillingSources(sources);
      setBillingData((prev) => ({
        ...prev,
        billingId: first.billingId,
        salesOrderId: first.salesOrderId,
        gstNumber: first.gstNumber,
        state: first.state,
        parentCompanyName: first.parentCompanyName,
        brandName: first.brandName || null,
        campaignName: first.campaignName || null,
        inventories: allInventories,
        billedInvs: sources.flatMap((s) => s.billedInvs),
        duePrintingAmount: totalDuePrint,
        dueMountingAmount: totalDueMount,
        consolidatedStamps: sources.map((s) => s.stamp).join(", "),
      }));
    } catch (err) {
      notification.error({ message: err.message });
      setConsolidatedBillingIds([]);
      setConsolidatedMode(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (consolidatedBillingIds?.length >= 2) {
      loadBillings();
    }
  }, [consolidatedBillingIds]);

  const handleFormChange = (fieldName, value) => {
    if (fieldName === "taxType") {
      setBillingData((prev) => ({
        ...prev,
        [fieldName]: value,
        SGST: value / 2,
        CGST: value / 2,
        IGST: 0,
      }));
      return;
    }
    if (fieldName === "IGST") {
      setBillingData((prev) => {
        if (!isGujaratBilling(prev)) {
          return { ...prev, IGST: value, SGST: 0, CGST: 0, taxType: 0 };
        }
        return { ...prev, IGST: value, taxType: 0 };
      });
      return;
    }
    if (fieldName === "refDate") {
      setBillingData((prev) => ({
        ...prev,
        refDate: value ? value.toISOString() : null,
      }));
      return;
    }
    setBillingData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleInvChange = (invId, field, value) => {
    setBillingData((prev) => ({
      ...prev,
      inventories: prev.inventories.map((inv) =>
        inv._id === invId ? { ...inv, [field]: value } : inv
      ),
    }));
  };

  const getProductionChargesForSubtotal = () => {
    const duePrinting = Number(billingData?.duePrintingAmount) || 0;
    const dueMounting = Number(billingData?.dueMountingAmount) || 0;
    const printingCharge =
      manualPrintingCharge !== undefined ? manualPrintingCharge : duePrinting;
    const mountingCharge =
      manualMountingCharge !== undefined ? manualMountingCharge : dueMounting;
    return {
      selectedPrinting:
        isPrintingChargeSelected && Number.isFinite(Number(printingCharge))
          ? Number(printingCharge)
          : 0,
      selectedMounting:
        isMountingChargeSelected && Number.isFinite(Number(mountingCharge))
          ? Number(mountingCharge)
          : 0,
    };
  };

  useEffect(() => {
    const inventoryById = new Map(
      (billingData?.inventories || []).map((inv) => [inv._id, inv])
    );
    let totalRate = selectedInventories.reduce((acc, sel) => {
      const row = inventoryById.get(sel._id) || sel;
      return acc + (Number(row?.specialRate) || 0);
    }, 0);
    const { selectedPrinting, selectedMounting } =
      getProductionChargesForSubtotal();
    totalRate += selectedPrinting + selectedMounting;
    setSubTotal(totalRate);
  }, [
    selectedInventories,
    billingData?.inventories,
    manualPrintingCharge,
    manualMountingCharge,
    isPrintingChargeSelected,
    isMountingChargeSelected,
    billingData?.duePrintingAmount,
    billingData?.dueMountingAmount,
  ]);

  useEffect(() => {
    const taxTotal =
      (Number(billingData?.IGST) || 0) +
      (Number(billingData?.SGST) || 0) +
      (Number(billingData?.CGST) || 0) +
      (Number(billingData?.municipalTax) || 0) +
      (Number(billingData?.otherTaxes) || 0) +
      (Number(billingData?.round) || 0) +
      (Number(billingData?.agencyCommission) || 0);
    setGrandTotal((subTotal * taxTotal) / 100 + subTotal);
  }, [billingData, subTotal]);

  const allocateCharges = (totalCharge, key) => {
    const totalDue = billingSources.reduce(
      (sum, s) => sum + (Number(s[key]) || 0),
      0
    );
    if (totalDue <= 0 || totalCharge <= 0) {
      return billingSources.map((s) => ({
        billingId: s.billingId,
        amount: 0,
      }));
    }
    return billingSources.map((s) => ({
      billingId: s.billingId,
      amount: Math.round((totalCharge * (Number(s[key]) || 0)) / totalDue),
    }));
  };

  const billingAmountExceedsDue = useMemo(() => {
    const byId = new Map(
      (billingData?.inventories || []).map((inv) => [inv._id, inv])
    );
    return selectedInventories.some((sel) => {
      const inv = byId.get(sel._id);
      if (!inv) return false;
      const rawDue = inv?.apiDueAmount;
      if (rawDue === undefined || rawDue === null || rawDue === "") return false;
      const due = Number(rawDue);
      if (!Number.isFinite(due)) return false;
      return Math.round(Number(inv?.specialRate) || 0) > Math.round(due);
    });
  }, [billingData?.inventories, selectedInventories]);

  const handleBack = () => {
    setConsolidatedBillingIds([]);
  };

  const handleConfirm = async () => {
    if (selectedInventories.length === 0) {
      notification.warning({ message: "Select at least one inventory line." });
      return;
    }
    setConfirmLoading(true);
    try {
      const inventoryById = new Map(
        (billingData?.inventories || []).map((inv) => [inv._id, inv])
      );

      const selectedInventoriesData = selectedInventories.map((sel) => {
        const inv = inventoryById.get(sel._id) || sel;
        return {
          invId: inv?.inventoryId ?? inv?.invId?._id ?? inv?.invId,
          billingInventoryId: inv._id,
          billingId: inv.sourceBillingId,
          salesOrderId: inv.sourceSalesOrderId,
          startDate: inv?.billingStartDate || inv?.startDate,
          endDate: inv?.billingEndDate || inv?.endDate,
          amount: Number(inv?.specialRate) || 0,
        };
      });

      const { selectedPrinting, selectedMounting } =
        getProductionChargesForSubtotal();
      const printAlloc = allocateCharges(
        selectedPrinting,
        "duePrintingAmount"
      );
      const mountAlloc = allocateCharges(
        selectedMounting,
        "dueMountingAmount"
      );

      const billingCharges = billingSources.map((s) => {
        const printEntry = printAlloc.find((p) => p.billingId === s.billingId);
        const mountEntry = mountAlloc.find((p) => p.billingId === s.billingId);
        return {
          billingId: s.billingId,
          printingCharge: printEntry?.amount || 0,
          mountingCharge: mountEntry?.amount || 0,
          isPrintingChargeIncluded: isPrintingChargeSelected,
          isMountingChargeIncluded: isMountingChargeSelected,
        };
      });

      const finalHsnCode = billingData?.HSN?.split("-")[0]?.trim() || 998361;

      const payload = {
        isConsolidated: true,
        billingIds: consolidatedBillingIds,
        salesOrderIds: billingSources.map((s) => s.salesOrderId),
        salesOrderId: billingSources[0]?.salesOrderId,
        id: billingSources[0]?.billingId,
        inventories: selectedInventoriesData,
        createdBy: userId,
        printingCharge: selectedPrinting,
        mountingCharge: selectedMounting,
        isPrintingChargeIncluded: isPrintingChargeSelected,
        isMountingChargeIncluded: isMountingChargeSelected,
        billingCharges,
        IGST: billingData?.IGST,
        SGST: billingData?.SGST,
        CGST: billingData?.CGST,
        aliasPartyName: billingData?.aliasPartyName?.trim() || null,
        HSN: parseInt(finalHsnCode, 10),
        remark:
          billingData?.invoiceRemark != null
            ? String(billingData.invoiceRemark).trim()
            : DEFAULT_INVOICE_REMARK,
        createdAt: billingData?.billingDate || new Date().toISOString(),
      };

      if (billingData?.invoiceSubRemark?.trim()) {
        payload.subRemark = billingData.invoiceSubRemark.trim();
      }
      if (billingData?.refNo) payload.refNo = billingData.refNo;
      if (billingData?.refDate) payload.refDate = billingData.refDate;
      if (billingData?.brandName) {
        payload.brandName = String(billingData.brandName).trim();
      }
      if (billingData?.campaignName) {
        payload.campaignName = String(billingData.campaignName).trim();
      }

      const partyGst = billingData?.gstNumber;
      if (!partyGst || String(partyGst).trim() === "") {
        payload.isB2C = true;
      }

      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/createBillingInvoice`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const responseData = await response.json();
      if (response.ok) {
        notification.success({
          message: "Bill created for all selected sales orders.",
        });
        setConsolidatedBillingIds([]);
        setConsolidatedMode(false);
        setFetchToggle((p) => !p);
      } else {
        throw new Error(responseData.message || "Failed to create bill.");
      }
    } catch (error) {
      notification.error({ message: error.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <IoArrowBack
          className="sales-order-arrow-back-icon"
          onClick={handleBack}
          style={{ cursor: "pointer" }}
        />
        <div style={{ marginLeft: "1rem", fontWeight: 600 }}>
          Create One Bill for Multiple SOs
        </div>
        <Tag color="blue" style={{ marginLeft: 12 }}>
          {billingSources.length} SOs
        </Tag>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Sales orders on this bill: ${billingData?.consolidatedStamps || ""}`}
      />

      <BillingForm
        billingData={billingData}
        handleFormChange={handleFormChange}
        log={null}
      />

      <InventoryList
        billingData={billingData}
        handleInvChange={handleInvChange}
        setBillingData={setBillingData}
        handleFormChange={handleFormChange}
        log={null}
        handleSelectionChange={setSelectedInventories}
      />

      <div
        className="grandTotal-TotalProductionCost-wrapper"
        style={{ marginTop: "1rem" }}
      >
        <Row>
          <Col span={12}>
            <GrandTotal
              billingData={billingData}
              subTotal={subTotal}
              handleFormChange={handleFormChange}
              grandTotal={grandTotal}
              setGrandTotal={setGrandTotal}
            />
          </Col>
          <Col span={12}>
            <TotalProductionCost
              selectedInventories={selectedInventories}
              totalInvoices={0}
              manualPrintingCharge={manualPrintingCharge}
              manualMountingCharge={manualMountingCharge}
              onPrintingChargeChange={setManualPrintingCharge}
              onMountingChargeChange={setManualMountingCharge}
              includedSubBDFPrintingCharge={0}
              includedSubBDFMountingCharge={0}
              isPrintingChargeSelected={isPrintingChargeSelected}
              isMountingChargeSelected={isMountingChargeSelected}
              onPrintingChargeSelectionChange={setIsPrintingChargeSelected}
              onMountingChargeSelectionChange={setIsMountingChargeSelected}
              duePrintingAmount={billingData?.duePrintingAmount || 0}
              dueMountingAmount={billingData?.dueMountingAmount || 0}
              isSplitBilling={false}
            />
          </Col>
        </Row>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 24,
          marginBottom: "2rem",
          paddingBottom: 8,
        }}
      >
        <Button onClick={handleBack}>Back</Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={confirmLoading || billingAmountExceedsDue}
        >
          Create Bill
        </Button>
      </div>
    </div>
  );
};

export default ConsolidatedBillingDetails;
